import { Button } from "@chakra-ui/button";
import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { Input, InputGroup, InputRightElement } from "@chakra-ui/input";
import { Box, Text, VStack } from "@chakra-ui/layout";
import { useToast } from "@chakra-ui/toast";
import axios from "axios";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { ChatState } from "../../Context/ChatProvider";
import AvatarUpload from "../ui/AvatarUpload";
import CodeInput from "../ui/CodeInput";

const Signup = () => {
  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);
  const toast = useToast();
  const history = useHistory();
  const { setUser } = ChatState();

  const [name, setName] = useState();
  const [email, setEmail] = useState();
  const [confirmpassword, setConfirmpassword] = useState();
  const [password, setPassword] = useState();
  const [pic, setPic] = useState("");
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState("form"); // form → code
  const [code, setCode] = useState("");
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const warn = (title, description) =>
    toast({
      title,
      description,
      status: "warning",
      duration: 5000,
      isClosable: true,
      position: "bottom",
    });

  const finish = (data) => {
    toast({
      title: "Вы в клубе!",
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "bottom",
    });
    setUser(data);
    localStorage.setItem("userInfo", JSON.stringify(data));
    const pending = localStorage.getItem("pendingInvite");
    if (pending) {
      localStorage.removeItem("pendingInvite");
      history.push(`/join/${pending}`);
    } else {
      history.push("/chats");
    }
  };

  // создание аккаунта (с кодом из письма или без него, если почта отключена)
  const register = async (withCode) => {
    const { data } = await axios.post(
      "/api/user",
      { name, email, password, pic, code: withCode },
      { headers: { "Content-type": "application/json" } }
    );
    finish(data);
  };

  const validateForm = () => {
    if (!name || !email || !password || !confirmpassword) {
      warn("Заполните все поля");
      return false;
    }
    if (password.length < 6) {
      warn("Пароль должен быть не короче 6 символов");
      return false;
    }
    if (password !== confirmpassword) {
      warn("Пароли не совпадают");
      return false;
    }
    return true;
  };

  // шаг 1 → запрос кода на почту
  const requestCode = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      await axios.post("/api/user/send-code", { email });
      setStep("code");
      setResendIn(60);
      toast({
        title: "Код отправлен",
        description: `Письмо ушло на ${email}`,
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      // почта на сервере недоступна — регистрируем сразу, как раньше
      if (error.response?.data?.message === "EMAIL_DISABLED") {
        try {
          await register();
        } catch (e) {
          toast({
            title: "Не получилось зарегистрироваться",
            description: e.response?.data?.message,
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "bottom",
          });
        }
      } else {
        toast({
          title: "Не удалось отправить код",
          description: error.response?.data?.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // шаг 2 → подтверждение кода и создание аккаунта
  const submitCode = async () => {
    if (code.length < 6) {
      warn("Введите код из шести цифр");
      return;
    }
    setLoading(true);
    try {
      await register(code);
    } catch (error) {
      toast({
        title: "Не получилось зарегистрироваться",
        description: error.response?.data?.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    try {
      await axios.post("/api/user/send-code", { email });
      setResendIn(60);
      setCode("");
      toast({
        title: "Новый код отправлен",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      toast({
        title: "Не удалось отправить код",
        description: error.response?.data?.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  if (step === "code") {
    return (
      <VStack spacing="14px">
        <Text fontSize="sm" color="rgba(36, 61, 39, 0.7)" textAlign="center">
          Мы отправили код на{" "}
          <Text as="span" fontWeight={600} color="#243D27">
            {email}
          </Text>
          . Загляните в почту — письмо приходит за минуту.
        </Text>
        <Text fontSize="xs" color="rgba(36, 61, 39, 0.55)" textAlign="center">
          Не пришло? Проверьте папку «Спам». Если письма нет и там — напишите
          администратору клуба, вас заведут вручную.
        </Text>

        <CodeInput value={code} onChange={setCode} onComplete={submitCode} />

        <Button
          colorScheme="burgundy"
          width="100%"
          h="46px"
          onClick={submitCode}
          isLoading={loading}
          boxShadow="0 10px 24px -10px rgba(141, 65, 75, 0.55)"
          _hover={{ bg: "#7A3841", transform: "translateY(-1px)" }}
          _active={{ bg: "#632D35", transform: "none" }}
        >
          Подтвердить и вступить
        </Button>

        <Box d="flex" w="100%" justifyContent="space-between" pt={1}>
          <Button
            variant="link"
            size="sm"
            colorScheme="pine"
            fontWeight={400}
            onClick={() => {
              setStep("form");
              setCode("");
            }}
          >
            Изменить почту
          </Button>
          <Button
            variant="link"
            size="sm"
            colorScheme="burgundy"
            fontWeight={400}
            isDisabled={resendIn > 0}
            onClick={resend}
          >
            {resendIn > 0 ? `Отправить ещё раз (${resendIn})` : "Отправить ещё раз"}
          </Button>
        </Box>
      </VStack>
    );
  }

  return (
    <VStack spacing="8px">
      <FormControl id="first-name" isRequired>
        <FormLabel color="#243D27">Имя</FormLabel>
        <Input
          value={name || ""}
          placeholder="Как вас называть в клубе"
          onChange={(e) => setName(e.target.value)}
        />
      </FormControl>
      <FormControl id="email" isRequired>
        <FormLabel color="#243D27">Почта</FormLabel>
        <Input
          value={email || ""}
          type="email"
          placeholder="you@example.com"
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormControl>
      <FormControl id="password" isRequired>
        <FormLabel color="#243D27">Пароль</FormLabel>
        <InputGroup size="md">
          <Input
            value={password || ""}
            type={show ? "text" : "password"}
            placeholder="Не короче 6 символов"
            onChange={(e) => setPassword(e.target.value)}
          />
          <InputRightElement width="4.5rem">
            <Button
              h="1.75rem"
              size="sm"
              variant="ghost"
              colorScheme="pine"
              onClick={handleClick}
            >
              {show ? "Скрыть" : "Показать"}
            </Button>
          </InputRightElement>
        </InputGroup>
      </FormControl>
      <FormControl id="confirm-password" isRequired>
        <FormLabel color="#243D27">Пароль ещё раз</FormLabel>
        <InputGroup size="md">
          <Input
            value={confirmpassword || ""}
            type={show ? "text" : "password"}
            placeholder="Повторите пароль"
            onChange={(e) => setConfirmpassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && requestCode()}
          />
          <InputRightElement width="4.5rem">
            <Button
              h="1.75rem"
              size="sm"
              variant="ghost"
              colorScheme="pine"
              onClick={handleClick}
            >
              {show ? "Скрыть" : "Показать"}
            </Button>
          </InputRightElement>
        </InputGroup>
      </FormControl>
      <FormControl id="pic">
        <FormLabel color="#243D27">Аватар (необязательно)</FormLabel>
        <AvatarUpload value={pic} onChange={setPic} />
      </FormControl>
      <Button
        colorScheme="burgundy"
        width="100%"
        h="46px"
        style={{ marginTop: 18 }}
        onClick={requestCode}
        isLoading={loading}
        boxShadow="0 10px 24px -10px rgba(141, 65, 75, 0.55)"
        _hover={{ bg: "#7A3841", transform: "translateY(-1px)" }}
        _active={{ bg: "#632D35", transform: "none" }}
      >
        Вступить в клуб
      </Button>
    </VStack>
  );
};

export default Signup;
